import { observer } from 'mobx-react-lite'

import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { useModals } from 'almanac-ui/Modal'
import dayjs from 'dayjs'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import {
  nameMap,
  iconMap,
  planIconColorMap,
} from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'

import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { useStripePaymentMethodUpdate } from 'hooks/useStripePaymentMethodUpdate'
import { useUpdateAutopilotSubscription } from 'hooks/useUpdateAutopilotSubscription'
import { getHref, getOrigin } from 'utils/location'
import Styles from './PlanCard.module.scss'
import { Pill } from 'components/Pill/Pill'
import LockOpen3 from 'blaze-ui/icons/24/LockOpen3'
import { formatPrice } from 'components/Settings/TieredBilling/utils'

/**
 * Plan card displaying current plan, payment info, and management controls.
 * Shown in billing settings next to CreditsCard when billing_usage_refresh flag is enabled.
 *
 * Cancel states:
 * - Partially canceled: cancel_at set + status active → can reactivate
 * - Fully canceled: status = 'canceled' → must subscribe to a new plan
 *
 * States and CTAs:
 * - Growth Paid / Starter Paid: Change Payment, Manage Plan
 * - Growth/Starter Partially Canceled: Reactivate (opens modal to reactivate)
 * - Growth/Starter Fully Canceled: Subscribe (opens modal to pick a new plan)
 * - Pioneer Paid: Manage Plan
 * - Pioneer Canceled (any): Subscribe (Pioneer cannot be reactivated)
 * - Free: Upgrade
 */
export const PlanCard = observer(() => {
  const { openModal } = useModals()
  const workspace = useCurrentWorkspace()
  const { currentSubscription, autopilotPlans } = useRootStore().tierUsageStore
  const { isUpdating } = useUpdateAutopilotSubscription()
  const successUrl = `${getOrigin()}/checkout-session?session_id={CHECKOUT_SESSION_ID}&redirect_to_billing=true`
  const { isProcessing, redirectToStripe } = useStripePaymentMethodUpdate(successUrl, getHref())

  const planKey = workspace?.is_ap_freemium
    ? PaymentPlanKey.Free
    : (currentSubscription?.plan as PaymentPlanKey | undefined)
  if (!planKey) return null
  const isFree = planKey === PaymentPlanKey.Free
  const isOnPioneer = planKey === PaymentPlanKey.VirtualMarketer
  const isOnEnterprise = planKey === PaymentPlanKey.BlazeAutopilotEnterprise
  const isTrialing = currentSubscription?.status === 'trialing'
  const isPartiallyCanceled = !!currentSubscription?.cancel_at && currentSubscription.status === 'active'
  const isFullyCanceled = currentSubscription?.status === 'canceled'
  const isTrialEnded = isFullyCanceled && !!currentSubscription?.trial_ended
  const showTrialLayout = isTrialing || isFullyCanceled

  const getTrialDetailText = () => {
    if (isTrialing) return `Your trial ends in ${currentSubscription?.trial_days_left} days`
    if (isTrialEnded) return 'Your trial has ended'
    return 'Subscription cancelled'
  }
  const hasCancelScheduled = isPartiallyCanceled
  const canceledPioneer = isOnPioneer && isPartiallyCanceled

  const PlanIconComponent = iconMap[planKey]
  const planIconColor = planIconColorMap[planKey]
  const planName = nameMap[planKey] ?? 'Plan'

  const starterPlan = autopilotPlans.find((p) => p.key === PaymentPlanKey.BlazeAutopilotStarter)
  const growthPlan = autopilotPlans.find((p) => p.key === PaymentPlanKey.BlazeAutopilotGrowth)
  const currentPlan = autopilotPlans.find((p) => p.key === planKey)

  const getPaymentInfoText = (): string | null => {
    if (!currentSubscription) return null

    if (isPartiallyCanceled) {
      return `Your plan ends on ${dayjs(currentSubscription.cancel_at).format('MMM D')} at ${dayjs(
        currentSubscription.cancel_at,
      ).format('h A')}`
    }
    if (isFullyCanceled) {
      return ``
    }
    if (currentSubscription.next_payment?.due_date) {
      return `Next payment on ${dayjs(currentSubscription.next_payment.due_date).format('MMM D, YYYY')}`
    }
    return null
  }

  const paymentInfoText = getPaymentInfoText()

  const handleManagePlan = () => {
    const canReactivate = isPartiallyCanceled && planKey !== PaymentPlanKey.VirtualMarketer

    if (canReactivate && currentSubscription) {
      let plan = null
      if (planKey === PaymentPlanKey.BlazeAutopilotGrowth) {
        plan = growthPlan
      } else if (planKey === PaymentPlanKey.BlazeAutopilotStarter) {
        plan = starterPlan
      }

      if (plan) {
        openModal(
          AutopilotPlansModal,
          {
            initialStep: 'selected',
            initialPlanAndPrice: { plan, price: currentSubscription.prices?.[0] },
            reactivate: true,
          },
          { newStack: true },
        )
        return
      }
    }

    openModal(AutopilotPlansModal, {}, { newStack: true })
  }

  const price = currentSubscription?.prices?.[0]
  // A one-time price has no interval, so there is no per-month figure to show.
  const monthlyPrice = price?.recurring ? formatPrice(price.unit_amount / price.recurring.interval_count / 100) : null

  const showChangePayment = !isFree && !isOnPioneer && !isOnEnterprise && !isTrialing
  const showUpgrade = isFree
  const showManagePlan = !isFree && !isOnEnterprise
  const canReactivate = isPartiallyCanceled && planKey !== PaymentPlanKey.VirtualMarketer

  let managePlanButtonLabel: string
  if (canReactivate) {
    managePlanButtonLabel = 'Reactivate'
  } else if (hasCancelScheduled) {
    managePlanButtonLabel = 'Subscribe'
  } else {
    managePlanButtonLabel = 'Manage Plan'
  }

  return (
    <div className={Styles.card}>
      <div className={Styles.headerRow}>
        <div className={Styles.headerLeft}>
          {PlanIconComponent && <PlanIconComponent color={planIconColor} />}
          <Heading level={5}>
            {planName} {isTrialing || isTrialEnded ? 'Trial' : 'Plan'}
          </Heading>
        </div>
        {isPartiallyCanceled && (
          <Pill size="sm" className={Styles.canceledPill}>
            Canceled
          </Pill>
        )}
      </div>
      <div className={Styles.divider} />
      {showTrialLayout ? (
        <div className={Styles.detailsSection}>
          <Text variant="secondary" color="var(--dark-60)">
            {getTrialDetailText()}
          </Text>
        </div>
      ) : (
        <>
          {paymentInfoText && (
            <div className={Styles.detailsSection}>
              <Text variant="secondary" color="var(--dark-60)">
                {paymentInfoText}
              </Text>
            </div>
          )}
          {isOnPioneer && (
            <div className={Styles.detailsSection}>
              <Text variant="secondary" color="var(--dark-60)">
                This plan won't be available again if you cancel or switch.
              </Text>
            </div>
          )}
        </>
      )}
      <div className={showTrialLayout ? Styles.trialControlsRow : Styles.controlsRow}>
        {showTrialLayout ? (
          <>
            <Button
              variant="primary"
              size="md"
              onPress={() =>
                openModal(
                  AutopilotPlansModal,
                  isTrialing && currentPlan
                    ? {
                        initialStep: 'selected',
                        initialPlanAndPrice: { plan: currentPlan, price: currentSubscription?.prices?.[0] },
                      }
                    : {},
                  { newStack: true },
                )
              }
            >
              Activate Plan
            </Button>
            {monthlyPrice && (
              <Text variant="secondary" color="var(--dark-80)">
                ${monthlyPrice} /mo
              </Text>
            )}
          </>
        ) : (
          <>
            {showChangePayment && (
              <Button
                variant="secondary"
                size="md"
                onPress={redirectToStripe}
                isDisabled={isProcessing || isUpdating}
                className={Styles.managePlanButton}
              >
                Change Payment
              </Button>
            )}
            {showUpgrade && (
              <Button
                variant="primary"
                size="md"
                onPress={() => openModal(AutopilotPlansModal, {}, { newStack: true })}
                frontIcon={LockOpen3}
              >
                Upgrade
              </Button>
            )}
            {canceledPioneer && (
              <Button
                variant="primary"
                size="md"
                onPress={() => openModal(AutopilotPlansModal, {}, { newStack: true })}
              >
                Activate Plan
              </Button>
            )}
            {showManagePlan && (
              <Button
                variant={canReactivate || hasCancelScheduled ? 'primary' : 'secondary'}
                size="md"
                onPress={handleManagePlan}
                isDisabled={isUpdating}
                className={Styles.managePlanButton}
              >
                {managePlanButtonLabel}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
})
